import { useQuery } from '@tanstack/react-query';
import Layout from '../components/layout/Layout';
import LeadershipSection from '../components/home/LeadershipSection';
import SEO from '../components/shared/SEO';
import JsonLd from '../components/shared/JsonLd';
import SectionLabel from '../components/shared/SectionLabel';
import { getFeaturedTeam } from '../api';
import '../styles/aboutpage.css';

const aboutContent = {
  label: 'About Us',
  title: 'Who We Are',
  paragraphs: [
    'Apex DeFi Labs Inc. is a global holding company operating at the convergence of decentralized finance, artificial intelligence, and next-generation digital assets. Founded with the vision of bridging traditional financial systems with emerging technologies, we build, acquire, and scale ventures that define the future of the financial and technology landscape.',
    'Our portfolio spans DeFi infrastructure, AI research, health technology, creative media, and capital markets — each operating as an independent brand under the Apex umbrella.',
  ],
};

export default function AboutPage() {
  const { data: team = [], isLoading: teamLoading } = useQuery({
    queryKey: ['team-featured'],
    queryFn: getFeaturedTeam,
  });

  return (
    <Layout>
      <div className="about-page">
        <SEO
          title="About Apex DeFi Labs Inc. — Vision, Mission & Leadership"
          description="Learn about Apex DeFi Labs Inc. — our vision for the future of finance, our mission to democratize digital assets, and the leadership team driving innovation."
          url="https://apexholdings.com/about"
          type="website"
          keywords="Apex DeFi Labs Inc. about, DeFi holding company, blockchain leadership, digital finance vision"
        />
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Apex DeFi Labs Inc.',
            url: 'https://apexholdings.com/about',
            description: 'Apex DeFi Labs Inc. corporate overview, vision, mission, and leadership team.',
            mainEntity: { '@type': 'Organization', name: 'Apex DeFi Labs Inc.' },
          }}
        />

        <section className="about-page-hero">
          <div className="about-page-hero-overlay" />

          <div className="about-page-hero-shell">
            <div className="about-page-hero-content">
              <div className="about-page-eyebrow">About Apex DeFi Labs Inc.</div>
              <h1 className="about-page-hero-title">
                Building the <em>Future</em>
              </h1>
              <p className="about-page-hero-sub">
                A global holding company at the convergence of decentralized finance, artificial
                intelligence, and next-generation digital assets.
              </p>
            </div>
          </div>
        </section>

        <section id="story" className="about-page-story">
          <div className="about-page-grid">
            <div className="about-page-visual">
              <div className="about-page-visual-card">
                <img
                  src="https://apexsign.s3.us-east-2.amazonaws.com/SiteAsset/about.jpeg"
                  alt="About Apex DeFi Labs Inc."
                  className="about-page-visual-img"
                />
              </div>
            </div>

            <div className="about-page-content">
              <div className="about-page-section-label">About Apex DeFi Labs Inc.</div>
              <h2 className="about-page-section-title">{aboutContent.title}</h2>

              <div className="about-page-tab-content">
                <div className="about-page-tab-heading">{aboutContent.label}</div>
                {aboutContent.paragraphs.map((paragraph, index) => (
                  <p key={index} className="about-page-tab-paragraph">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        <div id="leadership" className="about-page-leadership">
          {teamLoading || team.length > 0 ? (
            <LeadershipSection members={team} isLoading={teamLoading} />
          ) : (
            <section className="about-page-leadership-empty">
              <div className="about-page-leadership-shell">
                <SectionLabel>Leadership</SectionLabel>
                <h2 className="about-page-leadership-title">
                  The Team Behind <em>Apex</em>
                </h2>
                <p className="about-page-leadership-sub">
                  Seasoned executives driving innovation across decentralized finance, AI, and
                  next-generation technology.
                </p>

                <div className="about-page-leadership-card">
                  <div className="about-page-leadership-card-label">Leadership</div>
                  <p className="about-page-leadership-card-copy">
                    Leadership team information coming soon.
                  </p>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </Layout>
  );
}
